import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import SplitView from "../../components/splitView";
import Dimensions from "../../constants/styles/dimensions";
import { immutableRecords, PropTypeShapes } from "../../types";
import wrapWorkView from "../workView/wrapWorkView";
import NarrativeView from "./index";
import NarrativeSyncScroller from "./NarrativeSyncScroller";
import selectors from "./selectors";

class NarrativeViewLayout extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
  }

  requestViewBoundsForZoom(_zoom) {
    const height = this.props.containerRect.height;
    return immutableRecords.FlatRectRecord({
      left: 0,
      right: this.props.maxViewWidth,
      width: this.props.maxViewWidth,
      top: 0,
      height: height,
      bottom: height
    });
  }

  renderChildView = (view, siblingCount) => {
    const narrativeView = <NarrativeView key={view.viewId} viewId={view.viewId} />;

    // If we have already wrapped scrolling, then we just need to render this in a workView
    if (siblingCount > 1) {
      return wrapWorkView(
        narrativeView,
        view,
        siblingCount,
        this.props.maxHorizontalViews,
        this.props.maxVerticalViews,
        this.props.layoutDepth,
        this.props.workViewHandlers,
        null,
        this.props.viewLayout.orientation
      );
    }
    return narrativeView;
  };

  renderChildren = () => {
    const v = this.props.viewLayout;
    const children = v.children;
    const siblingCount = children.count();

    return children
      .map(v => {
        return this.renderChildView(v, siblingCount);
      })
      .toArray();
  };

  renderSplitView = () => {
    let splitProps;
    const children = this.props.viewLayout.get("children");
    const viewCount = children.count();
    splitProps = {
      vertical: false,
      constraints: children.map((c, index) =>
        immutableRecords.SplitConstraintsRecord({
          minSize: c.minSize.height,
          canResize: this.props.canViewResize && index !== viewCount - 1,
          hasDivider: index !== viewCount - 1,
          dividerVisible: index !== viewCount - 1,
          defaultSize: Math.max(c.size.height, 1),
          preferredSize: c.size.height,
          canCollapse: true,
          collapseSize: Dimensions.COLLAPSE_WORKVIEW_HEIGHT
        })
      )
    };

    const childComponents = [];

    const childElements = this.renderChildren();
    childComponents.push(...childElements);

    const splitView = (
      <SplitView key="narrativeLayoutSplitView" {...splitProps}>
        {childComponents}
      </SplitView>
    );
    if (children.count() === 1) {
      const view = children.first();
      return wrapWorkView(
        splitView,
        view,
        1,
        this.props.maxHorizontalViews,
        this.props.maxVerticalViews,
        this.props.layoutDepth,
        this.props.workViewHandlers,
        null,
        this.props.viewLayout.orientation
      );
    }

    return splitView;
  };

  renderSyncScroller = () => {
    return (
      <NarrativeSyncScroller
        key="syncScroller"
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        viewIds={this.props.viewIds}
      >
        {this.renderSplitView()}
      </NarrativeSyncScroller>
    );
  };

  render() {
    return this.props.viewLayout.size.height > Dimensions.COLLAPSE_WORKVIEW_HEIGHT
      ? this.renderSyncScroller()
      : this.renderSplitView();
  }
}

const mapStateToProps = (state, props) => {
  const scrollLeft = 0;
  const zoom = 1;

  const maxViewWidth = props.viewLayout.children.reduce((maxWidth, childView) => {
    const viewData = selectors.getNarrativeViewData(state.present, childView.viewId);
    if (viewData.size.width > maxWidth) {
      return viewData.size.width;
    }
    return maxWidth;
  }, 0);

  return {
    zoom,
    scrollLeft,
    maxViewWidth
  };
};

const mapDispatchToProps = _dispatch => {
  return {};
};

NarrativeViewLayout.propTypes = {
  viewLayout: PropTypeShapes.viewLayout,
  maxHorizontalViews: PropTypes.number.isRequired,
  maxVerticalViews: PropTypes.number.isRequired,
  canViewResize: PropTypes.bool.isRequired,
  zoom: PropTypes.number.isRequired,
  scrollLeft: PropTypes.number.isRequired,
  viewIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,

  // Used to work out the size of our scroll view
  maxViewWidth: PropTypes.number.isRequired,

  // How deep in the layout are we? Used to determine if we will allow splitting further
  layoutDepth: PropTypes.number.isRequired,

  // Handlers to be passed to workView
  workViewHandlers: PropTypes.object.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(NarrativeViewLayout));
